import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getEventoById, getInscritos, updateInscrito, deleteInscrito, addInscrito, downloadInscritosCsv, downloadCategoriasCsv, downloadInscritosLiftingCastCsv } from '../../../services/eventosService';
import { AfiliacionesService } from '../../../services/afiliacionesServices';
import { getUsuario } from '../../../services/adminService';
import { formatNombre, formatRut, formatPesoCategoriaLista, formatCategoriaEdad } from '../../../utils/formatoParticipantes';
import ConfirmModal from '../components/ConfirmModal';

const styles = {
  container: { padding: '24px', maxWidth: '1100px' },
  title: { margin: 0, fontSize: '24px', fontWeight: 700 },
  subtitle: { margin: '6px 0 20px', color: '#666', fontSize: '14px' },
  card: {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
    padding: '20px',
    marginTop: '16px',
  },
  row: { display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', marginBottom: '16px' },
  input: {
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '14px',
    minWidth: '180px',
  },
  btnPrimary: {
    padding: '10px 18px',
    background: '#1a4d8c',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 600,
  },
  btnGhost: {
    padding: '10px 16px',
    background: '#f4f6f8',
    color: '#111',
    border: '1px solid #ddd',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 600,
  },
  btnSecondary: {
    padding: '10px 16px',
    background: '#ffffff',
    color: '#1a4d8c',
    border: '1px solid #1a4d8c',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 700,
  },
  result: {
    marginTop: '16px',
    padding: '16px',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    background: '#fafafa',
  },
  badge: (estado) => ({
    display: 'inline-block',
    padding: '6px 12px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 700,
    background: estado === 'activa' ? '#e8f5e9' : estado === 'vencida' ? '#fff3e0' : '#ffebee',
    color: estado === 'activa' ? '#2e7d32' : estado === 'vencida' ? '#e65100' : '#c62828',
  }),
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: { padding: '10px 8px', borderBottom: '2px solid #e0e0e0', fontSize: '13px', color: '#555', textAlign: 'left', whiteSpace: 'nowrap' },
  td: { padding: '10px 8px', borderBottom: '1px solid #eee', whiteSpace: 'nowrap' },
  tableWrap: { overflowX: 'auto', maxHeight: '50vh', overflowY: 'auto' },
  btnSmall: {
    padding: '6px 10px',
    fontSize: '12px',
    fontWeight: 600,
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalBox: {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
    maxWidth: '520px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
    padding: '24px',
  },
  modalGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px', marginBottom: '16px' },
  modalFullWidth: { gridColumn: '1 / -1' },
};

const ESTADOS_INSCRITO = ['inscrito', 'confirmado', 'cancelado', 'descalificado', 'completado'];

// Mismas categorías que en Records (RecordsView.js)
const RANGOS_PESO = {
  masculino: ['52 kg', '56 kg', '60 kg', '67.5 kg', '75 kg', '82.5 kg', '90 kg', '100 kg', '110 kg', '125 kg', '140 kg', '+140 kg'],
  femenino: ['44 kg', '48 kg', '52 kg', '56 kg', '60 kg', '67.5 kg', '75 kg', '82.5 kg', '90 kg', '+90 kg'],
};
const CATEGORIAS_EDAD = [
  { id: 'subjunior', nombre: 'Sub-Junior' },
  { id: 'junior', nombre: 'Junior' },
  { id: 'open', nombre: 'Open' },
  { id: 'master', nombre: 'Master' },
];
const MODALIDADES = [
  { value: 'raw', label: 'Raw' },
  { value: 'classic_raw', label: 'Classic Raw' },
  { value: 'single_ply', label: 'Single Ply' },
  { value: 'bench', label: 'Only Bench' },
  { value: 'deadlift', label: 'Only Deadlift' },
];

const PESO_MASC = ['-52kg', '-56kg', '-60kg', '-67.5kg', '-75kg', '-82.5kg', '-90kg', '-100kg', '-110kg', '-125kg', '-140kg', '+140kg'];
const PESO_FEM = ['-44kg', '-48kg', '-52kg', '-56kg', '-60kg', '-67.5kg', '-75kg', '-82.5kg', '-90kg', '+90kg'];
const EDAD_OPCIONES = [
  { value: 'open', label: 'Open' },
  { value: 'sub_junior', label: 'Sub Junior (14-19 años)' },
  { value: 'junior', label: 'Junior (20-23 años)' },
  { value: 'master', label: 'Master (40+ años)' },
];
const LABEL_MODALIDAD = {
  raw: 'RAW (Rodilleras)',
  classic_raw: 'Classic Raw (Vendas)',
  single_ply: 'Single Ply',
  bench: 'Only Bench (sólo Banca)',
  deadlift: 'Only Deadlift (sólo Peso Muerto)',
  equipado: 'Equipado',
};

const CAMPOS_EDICION_LISTA = [
  ['nombre', 'nombre'],
  ['apellido', 'apellido'],
  ['rut', 'rut'],
  ['division', 'division'],
  ['categoria_peso_tested', 'categoria_peso_tested'],
  ['categoria_peso_untested', 'categoria_peso_untested'],
  ['categoria_edad', 'categoria_edad'],
  ['modalidad', 'modalidad'],
  ['numero_afiliacion', 'numero_afiliacion'],
  ['email', 'email'],
  ['telefono', 'telefono'],
  ['fecha_nacimiento', 'fecha_nacimiento'],
  ['genero', 'genero'],
  ['club', 'club'],
  ['modalidad_extra', 'modalidad_extra'],
  ['division_edad', 'division_edad'],
  ['division_edad_extra', 'division_edad_extra'],
  ['costo_key', 'costo_key'],
  ['costo_base', 'costo_base'],
  ['costo_extras', 'costo_extras'],
  ['costo_total', 'costo_total'],
];

function valorComoCsv(val) {
  if (val === null || val === undefined || val === '') return '—';
  if (typeof val === 'boolean') return val ? 'True' : 'False';
  return String(val);
}

/**
 * Fecha de inscripción legible en hora de Chile: "29-10-2026 10:23".
 * El backend guarda la hora en UTC sin zona ("2026-10-29T13:23:56.30"), por eso se marca como UTC.
 */
function formatFechaInscripcion(valor) {
  if (!valor) return '';
  const texto = String(valor);
  const conZona = /[zZ]|[+-]\d{2}:?\d{2}$/.test(texto) ? texto : `${texto}Z`;
  const fecha = new Date(conZona);
  if (Number.isNaN(fecha.getTime())) return texto;
  const partes = new Intl.DateTimeFormat('es-CL', {
    timeZone: 'America/Santiago',
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(fecha).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  return `${partes.day}-${partes.month}-${partes.year} ${partes.hour}:${partes.minute}`;
}

/**
 * Resumen de inscritos para el panel: confirmados, tested/untested y hombres/mujeres.
 * Las inscripciones canceladas no se cuentan en tested/untested ni en hombres/mujeres.
 */
function estadisticasInscritos(lista) {
  const txt = (v) => String(v ?? '').trim().toLowerCase();
  const est = { total: lista.length, confirmados: 0, activos: 0, tested: 0, untested: 0, hombres: 0, mujeres: 0 };
  lista.forEach((i) => {
    const estado = txt(i.estado);
    if (estado === 'confirmado') est.confirmados += 1;
    if (estado === 'cancelado') return;
    est.activos += 1;
    const division = txt(i.division);
    if (division.includes('untested') || (!division && i.categoria_peso_untested && !i.categoria_peso_tested)) est.untested += 1;
    else if (division.includes('tested') || (!division && i.categoria_peso_tested)) est.tested += 1;
    const genero = txt(i.genero);
    if (['f', 'femenino', 'female', 'mujer'].includes(genero)) est.mujeres += 1;
    else if (['m', 'masculino', 'male', 'hombre'].includes(genero)) est.hombres += 1;
  });
  return est;
}

/**
 * Columnas de la tabla de inscritos (formulario interno). Basadas en el CSV de inscritos, sin
 * evento_id, evento_nombre, categoria_edad, costo_key ni club_activo (siguen en el CSV detallado).
 * La última es DOTS: el mejor puntaje del atleta en los 2 últimos rankings anuales.
 */
function columnasListaComoCsv(evento) {
  return [
    { label: 'inscripcion_id', value: (i) => i.id },
    { label: 'numero_afiliacion', value: (i) => i.numero_afiliacion },
    { label: 'rut', value: (i) => i.rut || i.usuario_id },
    { label: 'nombre', value: (i) => i.nombre_pila },
    { label: 'apellido', value: (i) => i.apellido },
    { label: 'division', value: (i) => i.division },
    { label: 'categoria_peso_tested', value: (i) => i.categoria_peso_tested },
    { label: 'categoria_peso_untested', value: (i) => i.categoria_peso_untested },
    { label: 'modalidad', value: (i) => i.modalidad },
    { label: 'estado', value: (i) => i.estado },
    { label: 'email', value: (i) => i.email },
    { label: 'telefono', value: (i) => i.telefono },
    { label: 'fecha_nacimiento', value: (i) => i.fecha_nacimiento },
    { label: 'genero', value: (i) => i.genero },
    { label: 'club', value: (i) => i.club },
    { label: 'modalidad_extra', value: (i) => i.modalidad_extra },
    { label: 'division_edad', value: (i) => i.division_edad },
    { label: 'division_edad_extra', value: (i) => i.division_edad_extra },
    { label: 'costo_base', value: (i) => i.costo_base },
    { label: 'costo_extras', value: (i) => i.costo_extras },
    { label: 'costo_total', value: (i) => i.costo_total },
    { label: 'fecha_inscripcion', value: (i) => formatFechaInscripcion(i.created_at) },
    // Mejor DOTS del atleta en los 2 últimos rankings anuales (calculado en el backend)
    { label: 'DOTS', value: (i) => (i.dots != null ? Number(i.dots).toFixed(2) : '') },
  ];
}

const emptyCreateForm = () => ({
  rut: '',
  numero_afiliacion: '',
  nombre: '',
  apellido: '',
  fecha_nacimiento: '',
  telefono: '',
  email: '',
  club_declara: '',
  genero: '',
  division: '',
  modalidad: '',
  modalidad_extra: [],
  division_edad: '',
  division_edad_extra: 'no',
  categoria_peso: '',
});

function edadDesdeFecha(fechaStr) {
  if (!fechaStr) return null;
  const d = new Date(String(fechaStr).slice(0, 10));
  if (Number.isNaN(d.getTime())) return null;
  const hoy = new Date();
  let edad = hoy.getFullYear() - d.getFullYear();
  const m = hoy.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < d.getDate())) edad -= 1;
  return edad;
}

function divisionEdadPorEdad(edad) {
  if (edad === null || edad === undefined) return '';
  if (edad >= 40) return 'master';
  if (edad >= 20) return 'junior';
  if (edad >= 14) return 'sub_junior';
  return 'open';
}

function formatCLP(m) {
  return `$${Number(m || 0).toLocaleString('es-CL')}`;
}

/** Normaliza valor de peso guardado al valor de opción del select (ej. "90" → "90 kg", "140+" → "+140 kg"). */
function pesoGuardadoToOpcion(val) {
  if (val === null || val === undefined || val === '') return '';
  const s = String(val).trim().toLowerCase().replace(',', '.');
  const conPlus = /\+/.test(s) || s.includes('+');
  const numMatch = s.match(/(\d+\.?\d*)/);
  const num = numMatch ? parseFloat(numMatch[1]) : NaN;
  if (Number.isNaN(num)) return s;
  const sufijo = conPlus ? `+${Number.isInteger(num) ? num : num} kg` : `${Number.isInteger(num) ? num : num} kg`;
  const enHombres = RANGOS_PESO.masculino.includes(sufijo) || RANGOS_PESO.masculino.includes(`+${num} kg`);
  const enMujeres = RANGOS_PESO.femenino.includes(sufijo) || RANGOS_PESO.femenino.includes(`+${num} kg`);
  if (conPlus && num === 140) return '+140 kg';
  if (conPlus && num === 90) return '+90 kg';
  if (RANGOS_PESO.masculino.includes(sufijo)) return sufijo;
  if (RANGOS_PESO.femenino.includes(sufijo)) return sufijo;
  return conPlus ? `+${num} kg` : `${num} kg`;
}

/** Normaliza categoría edad guardada al id del select (ej. "SUB JUNIOR 15-19" → "subjunior"). */
function categoriaEdadGuardadaToId(val) {
  if (val === null || val === undefined || val === '') return '';
  const s = String(val).trim().toLowerCase();
  if (s.includes('sub') || s.includes('subjunior')) return 'subjunior';
  if (s.includes('junior') && !s.includes('sub')) return 'junior';
  if (s.includes('open')) return 'open';
  if (s.includes('master')) return 'master';
  const byId = CATEGORIAS_EDAD.find((c) => c.id === s);
  return byId ? byId.id : s;
}

const EventoParticipantesView = ({ eventoId, onBack }) => {
  const navigate = useNavigate();
  const [evento, setEvento] = useState(null);
  const [listaInscritos, setListaInscritos] = useState([]);
  const [loadingLista, setLoadingLista] = useState(false);
  const [rutBusqueda, setRutBusqueda] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [participante, setParticipante] = useState(null);
  const [afiliacion, setAfiliacion] = useState(null);
  const [error, setError] = useState(null);
  const [editingInscrito, setEditingInscrito] = useState(null);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [errorModal, setErrorModal] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [confirmDeleteInscrito, setConfirmDeleteInscrito] = useState(null);
  const [formDataCreate, setFormDataCreate] = useState(emptyCreateForm);
  const [lookingUpCreate, setLookingUpCreate] = useState(false);
  const [savingCreate, setSavingCreate] = useState(false);
  const [errorCreateModal, setErrorCreateModal] = useState(null);
  const [toast, setToast] = useState(null);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  const [downloadingCategorias, setDownloadingCategorias] = useState(false);
  const [downloadingLifters, setDownloadingLifters] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!eventoId) return;
    getEventoById(eventoId)
      .then((data) => { if (!cancelled) setEvento(data); })
      .catch(() => { if (!cancelled) setEvento(null); });
    return () => { cancelled = true; };
  }, [eventoId]);

  useEffect(() => {
    if (!eventoId) return;
    loadInscritos();
  }, [eventoId]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const loadInscritos = () => {
    if (!eventoId) return;
    setLoadingLista(true);
    getInscritos(Number(eventoId))
      .then((list) => setListaInscritos(Array.isArray(list) ? list : []))
      .catch(() => setListaInscritos([]))
      .finally(() => setLoadingLista(false));
  };

  const normDivisionForSelect = (val) => {
    const s = (val || '').trim().toLowerCase();
    if (s.includes('fem') || s === 'damas') return 'Femenino';
    if (s.includes('masc') || s.includes('varon') || s === 'masculino') return 'Masculino';
    return val && s ? val : 'Masculino';
  };

  const formularioInternoEvento = (evento?.formulario_inscripcion_tipo || '').toLowerCase() === 'interno';

  const openEdit = (i) => {
    setEditingInscrito(i);
    if (formularioInternoEvento) {
      const clubActivo = i.club_activo === true || String(i.club_activo).toLowerCase() === 'true'
        ? 'True'
        : (i.club_activo === false || String(i.club_activo).toLowerCase() === 'false' ? 'False' : '');
      setFormData({
        nombre: i.nombre_pila || '',
        apellido: i.apellido || '',
        rut: i.rut || i.usuario_id || '',
        division: i.division || '',
        categoria_peso_tested: i.categoria_peso_tested || '',
        categoria_peso_untested: i.categoria_peso_untested || '',
        categoria_edad: i.categoria_edad || '',
        modalidad: i.modalidad || '',
        estado: (i.estado || 'inscrito').toLowerCase(),
        numero_afiliacion: i.numero_afiliacion || '',
        email: i.email || '',
        telefono: i.telefono || '',
        fecha_nacimiento: i.fecha_nacimiento || '',
        genero: i.genero || '',
        club: i.club || '',
        modalidad_extra: i.modalidad_extra || '',
        division_edad: i.division_edad || '',
        division_edad_extra: i.division_edad_extra || '',
        costo_key: i.costo_key ?? '',
        costo_base: i.costo_base ?? '',
        costo_extras: i.costo_extras ?? '',
        costo_total: i.costo_total ?? '',
        club_activo: clubActivo,
      });
      setErrorModal(null);
      return;
    }
    const parsePesos = (val) => (val && String(val).trim()
      ? String(val).split(',').map((s) => pesoGuardadoToOpcion(s.trim())).filter(Boolean)
      : []);
    const testedArr = parsePesos(i.categoria_peso_tested);
    const untestedArr = parsePesos(i.categoria_peso_untested);
    const modalidadStr = (i.modalidad || '').trim().toLowerCase();
    const modalidadArr = modalidadStr
      ? modalidadStr.split(',').map((s) => s.trim()).filter((v) => MODALIDADES.some((m) => m.value === v))
      : [];
    setFormData({
      nombre: i.nombre_pila ?? (i.nombre || '').split(' ')[0] ?? '',
      apellido: i.apellido ?? (i.nombre || '').split(' ').slice(1).join(' ') ?? '',
      rut: i.rut || i.usuario_id || '',
      division: normDivisionForSelect(i.division),
      categoria_peso_tested: testedArr[0] ?? '',
      categoria_peso_untested: untestedArr[0] ?? '',
      categoria_edad: categoriaEdadGuardadaToId(i.categoria_edad) || 'open',
      modalidad: modalidadArr.length ? modalidadArr : ['raw'],
      dia_salida: i.dia_salida || '',
      tanda: i.tanda || '',
      estado: (i.estado || 'inscrito').toLowerCase(),
    });
    setErrorModal(null);
  };

  const closeEdit = () => {
    setEditingInscrito(null);
    setFormData({});
    setErrorModal(null);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (!formularioInternoEvento && field === 'categoria_peso_tested' && (value || '').trim() !== '') {
        next.categoria_peso_untested = '';
      }
      if (!formularioInternoEvento && field === 'categoria_peso_untested' && (value || '').trim() !== '') {
        next.categoria_peso_tested = '';
      }
      return next;
    });
  };

  const handleModalidadToggle = (value) => {
    setFormData((prev) => {
      const arr = Array.isArray(prev.modalidad) ? [...prev.modalidad] : (prev.modalidad ? [prev.modalidad] : []);
      const idx = arr.indexOf(value);
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(value);
      return { ...prev, modalidad: arr.length ? arr : ['raw'] };
    });
  };

  const handleSaveInscrito = async () => {
    if (!editingInscrito || !eventoId) return;
    setSaving(true);
    setErrorModal(null);
    try {
      const toStr = (v) => (v && String(v).trim() || null);
      const payload = formularioInternoEvento
        ? {
          nombre: toStr(formData.nombre),
          apellido: toStr(formData.apellido),
          rut: toStr(formData.rut),
          division: toStr(formData.division),
          categoria_peso_tested: toStr(formData.categoria_peso_tested),
          categoria_peso_untested: toStr(formData.categoria_peso_untested),
          categoria_edad: toStr(formData.categoria_edad),
          modalidad: toStr(formData.modalidad),
          estado: formData.estado || 'inscrito',
          numero_afiliacion: toStr(formData.numero_afiliacion),
          email: toStr(formData.email),
          telefono: toStr(formData.telefono),
          fecha_nacimiento: toStr(formData.fecha_nacimiento),
          genero: toStr(formData.genero),
          club: toStr(formData.club),
          modalidad_extra: toStr(formData.modalidad_extra),
          division_edad: toStr(formData.division_edad),
          division_edad_extra: toStr(formData.division_edad_extra),
          costo_key: toStr(formData.costo_key),
          costo_base: toStr(formData.costo_base),
          costo_extras: toStr(formData.costo_extras),
          costo_total: toStr(formData.costo_total),
          club_activo: toStr(formData.club_activo),
        }
        : {
          nombre: formData.nombre || null,
          apellido: formData.apellido || null,
          rut: formData.rut || null,
          division: formData.division || null,
          categoria_peso_tested: toStr(formData.categoria_peso_tested),
          categoria_peso_untested: toStr(formData.categoria_peso_untested),
          categoria_edad: formData.categoria_edad || null,
          modalidad: Array.isArray(formData.modalidad) ? (formData.modalidad.length ? formData.modalidad.join(',') : null) : (formData.modalidad || null),
          dia_salida: formData.dia_salida || null,
          tanda: formData.tanda || null,
          estado: formData.estado || 'inscrito',
        };
      await updateInscrito(eventoId, editingInscrito.id, payload);
      closeEdit();
      loadInscritos();
      setToast({ type: 'success', message: 'Participante actualizado correctamente.' });
    } catch (e) {
      setErrorModal(e.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDeleteInscrito = async () => {
    if (!eventoId || !confirmDeleteInscrito) return;
    try {
      await deleteInscrito(eventoId, confirmDeleteInscrito.id);
      setConfirmDeleteInscrito(null);
      loadInscritos();
      setToast({ type: 'success', message: 'Participante eliminado del evento.' });
    } catch (e) {
      setError(e.message || 'Error al eliminar');
      setConfirmDeleteInscrito(null);
    }
  };

  const modalidadesDisponibles = useMemo(() => {
    const mod = evento?.modalidad;
    return Array.isArray(mod) ? mod : [];
  }, [evento]);

  const modalidadExtraOpciones = useMemo(() => {
    const set = new Set((modalidadesDisponibles || []).map((m) => String(m || '').trim().toLowerCase()).filter(Boolean));
    const opts = [];
    if (set.has('bench')) opts.push({ value: 'bench', label: 'Only Bench (sólo Banca)' });
    if (set.has('deadlift')) opts.push({ value: 'deadlift', label: 'Only Deadlift (sólo Peso Muerto)' });
    return opts;
  }, [modalidadesDisponibles]);

  const openCreateModal = () => {
    setFormDataCreate(emptyCreateForm());
    setErrorCreateModal(null);
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setErrorCreateModal(null);
  };

  const setFieldCreate = (field, value) => {
    setFormDataCreate((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'fecha_nacimiento') {
        const computed = divisionEdadPorEdad(edadDesdeFecha(value));
        if (computed) {
          next.division_edad = computed;
          next.division_edad_extra = 'no';
        }
      }
      if (field === 'genero') next.categoria_peso = '';
      if (field === 'division_edad' && prev.division_edad_extra === value) {
        next.division_edad_extra = 'no';
      }
      return next;
    });
  };

  const toggleModalidadExtraCreate = (value) => {
    setFormDataCreate((prev) => {
      const arr = prev.modalidad_extra.includes(value)
        ? prev.modalidad_extra.filter((v) => v !== value)
        : [...prev.modalidad_extra, value];
      return { ...prev, modalidad_extra: arr };
    });
  };

  const completarDesdeRut = async (rutRaw) => {
    const rut = (rutRaw || '').trim().replace(/\./g, '').replace(/-/g, '').replace(/\s/g, '').toUpperCase();
    if (rut.length < 2) return;
    setLookingUpCreate(true);
    try {
      const u = await getUsuario(rut);
      const generoRaw = String(u.genero || '').toLowerCase();
      const genero = (generoRaw === 'f' || generoRaw === 'femenino')
        ? 'Femenino'
        : (generoRaw === 'm' || generoRaw === 'masculino')
          ? 'Masculino'
          : '';
      const afis = Array.isArray(u.afiliaciones) ? u.afiliaciones : [];
      const activa = afis.find((a) => String(a.estado || '').toLowerCase() === 'activa');
      const club = u.clubes && u.clubes[0] && u.clubes[0].nombre ? u.clubes[0].nombre : 'Sin club';
      const fecha = u.fecha_nacimiento ? String(u.fecha_nacimiento).slice(0, 10) : '';
      setFormDataCreate((prev) => ({
        ...prev,
        nombre: u.nombre || prev.nombre,
        apellido: u.apellido || prev.apellido,
        email: u.email || prev.email,
        telefono: u.telefono || prev.telefono,
        fecha_nacimiento: fecha || prev.fecha_nacimiento,
        genero: genero || prev.genero,
        numero_afiliacion: activa?.numero_afiliacion ? String(activa.numero_afiliacion) : prev.numero_afiliacion,
        club_declara: club,
        division_edad: fecha ? (divisionEdadPorEdad(edadDesdeFecha(fecha)) || prev.division_edad) : prev.division_edad,
        division_edad_extra: fecha ? 'no' : prev.division_edad_extra,
      }));
    } catch {
      // El admin puede completar los datos a mano si el RUT no está en el sistema.
    } finally {
      setLookingUpCreate(false);
    }
  };

  const montoAltaAdmin = useMemo(() => {
    if (!evento || !formDataCreate.division) return null;
    const det = (evento.costos_inscripcion_detalle && typeof evento.costos_inscripcion_detalle === 'object')
      ? evento.costos_inscripcion_detalle
      : {};
    const hasClub = !!(formDataCreate.club_declara && formDataCreate.club_declara !== 'Sin club');
    const key = formDataCreate.division === 'tested'
      ? (hasClub ? 'tested_con_club' : 'tested_independiente')
      : (hasClub ? 'untested_con_club' : 'untested_independiente');
    const base = Number(det[key] || 0);
    const extraMod = (evento.modalidad_extra_habilitada && formDataCreate.modalidad_extra.length > 0)
      ? Number(evento.modalidad_extra_monto || 0) * formDataCreate.modalidad_extra.length
      : 0;
    const extraEdad = (evento.edad_extra_habilitada && formDataCreate.division_edad_extra && formDataCreate.division_edad_extra !== 'no')
      ? Number(evento.modalidad_extra_monto || 0)
      : 0;
    return {
      items: [
        ...(base > 0 ? [{ label: 'Inscripción', amount: base }] : []),
        ...(extraMod > 0 ? [{ label: 'Modalidad extra', amount: extraMod }] : []),
        ...(extraEdad > 0 ? [{ label: 'División de edad extra', amount: extraEdad }] : []),
      ],
      total: base + extraMod + extraEdad,
    };
  }, [evento, formDataCreate.division, formDataCreate.club_declara, formDataCreate.modalidad_extra, formDataCreate.division_edad_extra]);

  const handleCreateInscrito = async () => {
    if (!eventoId) return;
    const rut = (formDataCreate.rut || '').trim().replace(/\./g, '').replace(/-/g, '').toUpperCase();
    if (!rut) {
      setErrorCreateModal('El RUT es obligatorio');
      return;
    }
    if (!formDataCreate.nombre.trim() || !formDataCreate.apellido.trim()) {
      setErrorCreateModal('Nombre y apellido son obligatorios.');
      return;
    }
    if (!formDataCreate.division) {
      setErrorCreateModal('Selecciona una división (TESTED o UNTESTED).');
      return;
    }
    if (!formDataCreate.genero) {
      setErrorCreateModal('Selecciona género.');
      return;
    }
    if (!formDataCreate.categoria_peso) {
      setErrorCreateModal('Selecciona categoría de peso.');
      return;
    }
    if (!formDataCreate.modalidad) {
      setErrorCreateModal('Selecciona modalidad.');
      return;
    }
    if (!formDataCreate.division_edad) {
      setErrorCreateModal('Selecciona división de edad.');
      return;
    }
    setSavingCreate(true);
    setErrorCreateModal(null);
    try {
      await addInscrito(eventoId, {
        rut,
        numero_afiliacion: formDataCreate.numero_afiliacion,
        nombre: formDataCreate.nombre.trim(),
        apellido: formDataCreate.apellido.trim(),
        fecha_nacimiento: formDataCreate.fecha_nacimiento,
        telefono: formDataCreate.telefono,
        email: formDataCreate.email,
        club_declara: formDataCreate.club_declara && formDataCreate.club_declara !== 'Sin club'
          ? formDataCreate.club_declara
          : null,
        genero: formDataCreate.genero,
        division: formDataCreate.division,
        modalidad: formDataCreate.modalidad,
        modalidad_extra: formDataCreate.modalidad_extra.length > 0 ? formDataCreate.modalidad_extra.join(',') : 'no',
        division_edad: formDataCreate.division_edad,
        division_edad_extra: formDataCreate.division_edad_extra,
        categoria_edad: formDataCreate.division_edad,
        categoria_peso_tested: formDataCreate.division === 'tested' ? formDataCreate.categoria_peso : null,
        categoria_peso_untested: formDataCreate.division === 'untested' ? formDataCreate.categoria_peso : null,
        estado: 'confirmado',
      });
      closeCreateModal();
      loadInscritos();
      setToast({ type: 'success', message: 'Atleta agregado al evento. El pago queda a cargo del administrador.' });
    } catch (e) {
      setErrorCreateModal(e.message || 'Error al agregar atleta');
    } finally {
      setSavingCreate(false);
    }
  };

  const buscar = async () => {
    const rut = (rutBusqueda || '').trim().replace(/\s/g, '');
    if (!rut) {
      setError('Ingresa un RUT para buscar.');
      setParticipante(null);
      setAfiliacion(null);
      return;
    }
    setError(null);
    setParticipante(null);
    setAfiliacion(null);
    setBuscando(true);
    try {
      const [inscritosRes, afilRes] = await Promise.all([
        getInscritos(Number(eventoId)),
        AfiliacionesService.listarAdmin(1, 20, rut),
      ]);
      const inscritos = Array.isArray(inscritosRes) ? inscritosRes : [];
      const rutNorm = rut.replace(/\./g, '').replace(/-/g, '').toUpperCase();
      const coincideRut = (r) => {
        const s = (r || '').replace(/\./g, '').replace(/-/g, '').toUpperCase();
        return s === rutNorm || s === rutNorm.replace(/[Kk]$/, '') || (rutNorm.endsWith('K') && s === rutNorm.slice(0, -1));
      };
      const inscrito = inscritos.find((i) => coincideRut(i.rut || i.usuario_id));
      if (inscrito) {
        setParticipante({
          nombre: inscrito.nombre || '—',
          rut: inscrito.rut || inscrito.usuario_id,
          inscrito: true,
        });
      } else {
        setParticipante({ nombre: null, rut, inscrito: false });
      }
      const items = (afilRes?.items || []).filter(
        (a) => a.usuario && coincideRut(a.usuario.rut)
      );
      if (items.length > 0) {
        const activa = items.find((a) => (a.estado || '').toLowerCase() === 'activa');
        setAfiliacion(activa || items[0]);
      } else {
        setAfiliacion(null);
      }
    } catch (e) {
      setError(e.message || 'Error al buscar.');
    } finally {
      setBuscando(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button type="button" style={styles.btnGhost} onClick={onBack}>
          Volver a eventos
        </button>
        <button
          type="button"
          style={styles.btnSecondary}
          onClick={() => navigate(`/admin?view=eventos&primetime=${eventoId}`)}
          title="Ranking de inscritos por DOTS para elegir el prime time"
        >
          Prime time
        </button>
      </div>
      {evento && (
        <h1 style={styles.title}>{evento.nombre || 'Evento'}</h1>
      )}
      <p style={styles.subtitle}>
        Busca un participante por RUT y revisa si su afiliación está activa.
      </p>

      <div style={styles.card}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '14px' }}>
          RUT del participante
        </label>
        <div style={styles.row}>
          <input
            type="text"
            style={styles.input}
            value={rutBusqueda}
            onChange={(e) => setRutBusqueda(e.target.value)}
            placeholder="Ej. 17.492.726-K o 17492726K"
            onKeyDown={(e) => e.key === 'Enter' && buscar()}
          />
          <button type="button" style={styles.btnPrimary} onClick={buscar} disabled={buscando}>
            {buscando ? 'Buscando…' : 'Buscar'}
          </button>
        </div>
        {error && <p style={{ color: '#c62828', margin: '8px 0 0', fontWeight: 600 }}>{error}</p>}

        {listaInscritos.length > 0 && (() => {
          const est = estadisticasInscritos(listaInscritos);
          const chip = (label, valor, color) => (
            <span
              key={label}
              style={{
                display: 'inline-flex', alignItems: 'baseline', gap: 6, padding: '6px 12px',
                borderRadius: 999, background: '#f5f5f5', border: '1px solid #e5e5e5', fontSize: 13,
              }}
            >
              <span style={{ color: '#666' }}>{label}</span>
              <strong style={{ color, fontSize: 15 }}>{valor}</strong>
            </span>
          );
          return (
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #eee' }}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
                Resumen de inscritos ({est.activos} sin contar cancelados)
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {chip('Tested', est.tested, '#1565c0')}
                {chip('Untested', est.untested, '#6a1b9a')}
                <span style={{ width: 1, background: '#e5e5e5', margin: '0 4px' }} aria-hidden="true" />
                {chip('Hombres', est.hombres, '#111')}
                {chip('Mujeres', est.mujeres, '#c2185b')}
                <span style={{ width: 1, background: '#e5e5e5', margin: '0 4px' }} aria-hidden="true" />
                {chip('Confirmados', est.confirmados, '#2e7d32')}
              </div>
            </div>
          );
        })()}

        {(participante !== null || afiliacion !== null) && !buscando && (
          <div style={styles.result}>
            {participante !== null && (
              <div style={{ marginBottom: afiliacion ? '12px' : 0 }}>
                <strong>Participante:</strong>{' '}
                {participante.inscrito ? (
                  <span>{participante.nombre} (RUT {formatRut(participante.rut)}) — <span style={{ color: '#2e7d32' }}>Inscrito en este evento</span></span>
                ) : (
                  <span style={{ color: '#666' }}>No inscrito en este evento. RUT buscado: {formatRut(participante.rut)}</span>
                )}
              </div>
            )}
            <div>
              <strong>Afiliación:</strong>{' '}
              {afiliacion ? (
                <span>
                  <span style={styles.badge(afiliacion.estado)}>
                    {(afiliacion.estado || '').toLowerCase() === 'activa' ? 'Activa' : (afiliacion.estado || 'N/A')}
                  </span>
                  {afiliacion.numero_afiliacion && ` — Nº ${afiliacion.numero_afiliacion}`}
                  {afiliacion.fecha_fin && ` — Vence ${new Date(afiliacion.fecha_fin).toLocaleDateString('es-CL')}`}
                </span>
              ) : (
                <span style={{ color: '#c62828', fontWeight: 600 }}>Sin afiliación activa</span>
              )}
            </div>
          </div>
        )}
      </div>

      <div style={styles.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Lista completa de participantes</h3>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            {evento?.formulario_inscripcion_tipo === 'interno' && (
              <button
                type="button"
                style={styles.btnSecondary}
                disabled={downloadingLifters}
                title="Inscritos en el formato de importación de atletas de LiftingCast, asignados a las mismas categorías de 'Descargar categorías'"
                onClick={async () => {
                  setDownloadingLifters(true);
                  try {
                    const r = await downloadInscritosLiftingCastCsv(Number(eventoId));
                    let message = Number.isFinite(r.atletas) ? `Inscritos descargados: ${r.atletas} atletas.` : 'Inscritos descargados.';
                    if (r.omitidos > 0) {
                      message += ` ${r.omitidos} sin categoría (falta género, división o modalidad válida): revísalos en LiftingCast.`;
                    }
                    setToast({ type: r.omitidos > 0 ? 'warning' : 'success', message });
                  } catch (e) {
                    setToast({ type: 'error', message: e.message || 'Error al descargar inscritos' });
                  } finally {
                    setDownloadingLifters(false);
                  }
                }}
              >
                {downloadingLifters ? 'Descargando…' : 'Descargar inscritos'}
              </button>
            )}
            {evento?.formulario_inscripcion_tipo === 'interno' && (
              <button
                type="button"
                style={{ ...styles.btnSecondary, opacity: 0.85 }}
                disabled={downloadingCsv}
                title="Todas las columnas del sistema (costos, estado, fechas…), incluye cancelados"
                onClick={async () => {
                  setDownloadingCsv(true);
                  try {
                    await downloadInscritosCsv(Number(eventoId));
                    setToast({ type: 'success', message: 'CSV detallado descargado.' });
                  } catch (e) {
                    setToast({ type: 'error', message: e.message || 'Error al descargar CSV' });
                  } finally {
                    setDownloadingCsv(false);
                  }
                }}
              >
                {downloadingCsv ? 'Descargando…' : 'CSV detallado'}
              </button>
            )}
            {evento?.formulario_inscripcion_tipo === 'interno' && (
              <button
                type="button"
                style={styles.btnSecondary}
                disabled={downloadingCategorias}
                title="Categorías para importar en LiftingCast: solo las que tienen inscritos, con todas sus categorías de peso, más las OVERALL de las que tienen 5 o más inscritos"
                onClick={async () => {
                  setDownloadingCategorias(true);
                  try {
                    const r = await downloadCategoriasCsv(Number(eventoId));
                    const partes = [];
                    if (Number.isFinite(r.base)) partes.push(`${r.base} categorías base`);
                    if (Number.isFinite(r.overall)) partes.push(`${r.overall} OVERALL`);
                    let message = `Categorías descargadas${partes.length ? `: ${partes.join(' y ')}` : ''}.`;
                    if (r.omitidos > 0) {
                      message += ` ${r.omitidos} inscrito(s) no se pudieron clasificar (falta género, división o modalidad válida).`;
                    }
                    setToast({ type: r.omitidos > 0 ? 'warning' : 'success', message });
                  } catch (e) {
                    setToast({ type: 'error', message: e.message || 'Error al descargar categorías' });
                  } finally {
                    setDownloadingCategorias(false);
                  }
                }}
              >
                {downloadingCategorias ? 'Descargando…' : 'Descargar categorías'}
              </button>
            )}
            <button type="button" style={styles.btnPrimary} onClick={openCreateModal}>
              + Agregar atleta
            </button>
          </div>
        </div>
        {loadingLista ? (
          <p style={{ color: '#666', margin: 0 }}>Cargando lista…</p>
        ) : listaInscritos.length === 0 ? (
          <p style={{ color: '#666', margin: 0 }}>Aún no hay inscritos en este evento.</p>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  {(evento?.formulario_inscripcion_tipo || '').toLowerCase() === 'interno' ? (
                    columnasListaComoCsv(evento).map((col) => (
                      <th key={col.label} style={styles.th}>{col.label}</th>
                    ))
                  ) : (
                    <>
                      <th style={styles.th}>Nombre</th>
                      <th style={styles.th}>RUT</th>
                      <th style={styles.th}>División</th>
                      <th style={styles.th}>Categoría peso TESTED</th>
                      <th style={styles.th}>Categoría peso UNTESTED</th>
                      <th style={styles.th}>Categoría edad</th>
                      <th style={styles.th}>Modalidad</th>
                      <th style={styles.th}>Día</th>
                      <th style={styles.th}>Tanda</th>
                      <th style={styles.th}>Estado</th>
                    </>
                  )}
                  <th style={styles.th}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {listaInscritos.map((i) => {
                  const formularioInterno = (evento?.formulario_inscripcion_tipo || '').toLowerCase() === 'interno';
                  return (
                  <tr key={i.id}>
                    {formularioInterno ? (
                      columnasListaComoCsv(evento).map((col) => (
                        <td key={col.label} style={styles.td}>{valorComoCsv(col.value(i))}</td>
                      ))
                    ) : (
                      <>
                        <td style={styles.td}>{formatNombre(i.nombre)}</td>
                        <td style={styles.td}>{formatRut(i.rut || i.usuario_id)}</td>
                        <td style={styles.td}>{i.division || '—'}</td>
                        <td style={styles.td}>{formatPesoCategoriaLista(i.categoria_peso_tested, i.division)}</td>
                        <td style={styles.td}>{formatPesoCategoriaLista(i.categoria_peso_untested, i.division)}</td>
                        <td style={styles.td}>
                          {(() => {
                            const edad = formatCategoriaEdad(i.categoria_edad);
                            return edad === '—' ? '—' : edad.toUpperCase();
                          })()}
                        </td>
                        <td style={styles.td}>
                          {(i.modalidad || '').trim() ? (i.modalidad || '').trim().replace(/,/g, ', ').toUpperCase() : '—'}
                        </td>
                        <td style={styles.td}>{i.dia_salida || '—'}</td>
                        <td style={styles.td}>{i.tanda || '—'}</td>
                        <td style={styles.td}>{i.estado || 'inscrito'}</td>
                      </>
                    )}
                    <td style={styles.td}>
                      <button
                        type="button"
                        style={{ ...styles.btnSmall, background: '#1a4d8c', color: '#fff' }}
                        onClick={() => openEdit(i)}
                      >
                        Editar
                      </button>
                      {' '}
                      <button
                        type="button"
                        style={{ ...styles.btnSmall, background: '#c62828', color: '#fff' }}
                        onClick={() => setConfirmDeleteInscrito(i)}
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {listaInscritos.length > 0 && (() => {
          const est = estadisticasInscritos(listaInscritos);
          return (
            <p style={{ margin: '12px 0 0', fontSize: '13px', color: '#666' }}>
              {est.total} participante{est.total !== 1 ? 's' : ''} inscrito{est.total !== 1 ? 's' : ''}
              {' · '}
              <strong style={{ color: '#2e7d32' }}>{est.confirmados} confirmado{est.confirmados !== 1 ? 's' : ''}</strong>.
            </p>
          );
        })()}
      </div>

      {editingInscrito && (
        <div style={styles.modalOverlay} onClick={closeEdit}>
          <div style={{ ...styles.modalBox, maxWidth: formularioInternoEvento ? 760 : 520 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: 700 }}>Editar participante</h3>
            {formularioInternoEvento ? (
              <div style={styles.modalGrid}>
                {CAMPOS_EDICION_LISTA.map(([key, label]) => (
                  <label key={key}>
                    <span style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#555' }}>{label}</span>
                    <input
                      type="text"
                      style={{ ...styles.input, width: '100%', minWidth: 0 }}
                      value={formData[key] ?? ''}
                      onChange={(e) => handleFormChange(key, e.target.value)}
                    />
                  </label>
                ))}
                <label>
                  <span style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#555' }}>estado</span>
                  <select
                    style={{ ...styles.input, width: '100%', minWidth: 0 }}
                    value={formData.estado ?? 'inscrito'}
                    onChange={(e) => handleFormChange('estado', e.target.value)}
                  >
                    {ESTADOS_INSCRITO.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#555' }}>club_activo</span>
                  <select
                    style={{ ...styles.input, width: '100%', minWidth: 0 }}
                    value={formData.club_activo ?? ''}
                    onChange={(e) => handleFormChange('club_activo', e.target.value)}
                  >
                    <option value="">—</option>
                    <option value="True">True</option>
                    <option value="False">False</option>
                  </select>
                </label>
              </div>
            ) : (
            <div style={styles.modalGrid}>
              <label style={styles.modalFullWidth}>
                <span style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#555' }}>Nombre</span>
                <input
                  type="text"
                  style={{ ...styles.input, width: '100%' }}
                  value={formData.nombre ?? ''}
                  onChange={(e) => handleFormChange('nombre', e.target.value)}
                />
              </label>
              <label style={styles.modalFullWidth}>
                <span style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#555' }}>Apellido</span>
                <input
                  type="text"
                  style={{ ...styles.input, width: '100%' }}
                  value={formData.apellido ?? ''}
                  onChange={(e) => handleFormChange('apellido', e.target.value)}
                />
              </label>
              <label style={styles.modalFullWidth}>
                <span style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#555' }}>RUT</span>
                <input
                  type="text"
                  style={{ ...styles.input, width: '100%' }}
                  value={formData.rut ?? ''}
                  onChange={(e) => handleFormChange('rut', e.target.value)}
                />
              </label>
              <label>
                <span style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#555' }}>División</span>
                <select
                  style={{ ...styles.input, width: '100%' }}
                  value={formData.division ?? ''}
                  onChange={(e) => handleFormChange('division', e.target.value)}
                >
                  <option value="Masculino">Hombres</option>
                  <option value="Femenino">Mujeres</option>
                </select>
              </label>
              <label>
                <span style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#555' }}>Estado</span>
                <select
                  style={{ ...styles.input, width: '100%' }}
                  value={formData.estado ?? 'inscrito'}
                  onChange={(e) => handleFormChange('estado', e.target.value)}
                >
                  {ESTADOS_INSCRITO.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </label>
              <label>
                <span style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#555' }}>Categoría peso TESTED</span>
                <select
                  style={{
                    ...styles.input,
                    width: '100%',
                    opacity: (formData.categoria_peso_untested ?? '').trim() ? 0.6 : 1,
                    cursor: (formData.categoria_peso_untested ?? '').trim() ? 'not-allowed' : 'pointer',
                  }}
                  value={formData.categoria_peso_tested ?? ''}
                  onChange={(e) => handleFormChange('categoria_peso_tested', e.target.value)}
                  disabled={!!(formData.categoria_peso_untested ?? '').trim()}
                >
                  <option value="">— Sin TESTED —</option>
                  {(formData.division || '').toLowerCase().includes('fem') ? (
                    RANGOS_PESO.femenino.map((p) => <option key={p} value={p}>{p}</option>)
                  ) : (formData.division || '').toLowerCase().includes('masc') || (formData.division || '').toLowerCase().includes('masculino') ? (
                    RANGOS_PESO.masculino.map((p) => <option key={p} value={p}>{p}</option>)
                  ) : (
                    <>
                      <optgroup label="Hombres">
                        {RANGOS_PESO.masculino.map((p) => <option key={`m-${p}`} value={p}>{p}</option>)}
                      </optgroup>
                      <optgroup label="Mujeres">
                        {RANGOS_PESO.femenino.map((p) => <option key={`f-${p}`} value={p}>{p}</option>)}
                      </optgroup>
                    </>
                  )}
                </select>
              </label>
              <label>
                <span style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#555' }}>Categoría peso UNTESTED</span>
                <select
                  style={{
                    ...styles.input,
                    width: '100%',
                    opacity: (formData.categoria_peso_tested ?? '').trim() ? 0.6 : 1,
                    cursor: (formData.categoria_peso_tested ?? '').trim() ? 'not-allowed' : 'pointer',
                  }}
                  value={formData.categoria_peso_untested ?? ''}
                  onChange={(e) => handleFormChange('categoria_peso_untested', e.target.value)}
                  disabled={!!(formData.categoria_peso_tested ?? '').trim()}
                >
                  <option value="">— Sin UNTESTED —</option>
                  {(formData.division || '').toLowerCase().includes('fem') ? (
                    RANGOS_PESO.femenino.map((p) => <option key={p} value={p}>{p}</option>)
                  ) : (formData.division || '').toLowerCase().includes('masc') || (formData.division || '').toLowerCase().includes('masculino') ? (
                    RANGOS_PESO.masculino.map((p) => <option key={p} value={p}>{p}</option>)
                  ) : (
                    <>
                      <optgroup label="Hombres">
                        {RANGOS_PESO.masculino.map((p) => <option key={`m-${p}`} value={p}>{p}</option>)}
                      </optgroup>
                      <optgroup label="Mujeres">
                        {RANGOS_PESO.femenino.map((p) => <option key={`f-${p}`} value={p}>{p}</option>)}
                      </optgroup>
                    </>
                  )}
                </select>
              </label>
              <label>
                <span style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#555' }}>Categoría edad</span>
                <select
                  style={{ ...styles.input, width: '100%' }}
                  value={formData.categoria_edad ?? 'open'}
                  onChange={(e) => handleFormChange('categoria_edad', e.target.value)}
                >
                  {CATEGORIAS_EDAD.map((c) => (
                    <option key={c.id} value={c.id}>{c.nombre}</option>
                  ))}
                </select>
              </label>
              <label style={styles.modalFullWidth}>
                <span style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#555' }}>Modalidad (puede elegir varias)</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
                  {MODALIDADES.map((m) => {
                    const arr = Array.isArray(formData.modalidad) ? formData.modalidad : (formData.modalidad ? [formData.modalidad] : []);
                    const checked = arr.includes(m.value);
                    return (
                      <label key={m.value} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleModalidadToggle(m.value)}
                        />
                        {m.label}
                      </label>
                    );
                  })}
                </div>
              </label>
              <label>
                <span style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#555' }}>Día</span>
                <input
                  type="text"
                  style={{ ...styles.input, width: '100%' }}
                  value={formData.dia_salida ?? ''}
                  onChange={(e) => handleFormChange('dia_salida', e.target.value)}
                  placeholder="1, 2, …"
                />
              </label>
              <label>
                <span style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#555' }}>Tanda</span>
                <input
                  type="text"
                  style={{ ...styles.input, width: '100%' }}
                  value={formData.tanda ?? ''}
                  onChange={(e) => handleFormChange('tanda', e.target.value)}
                  placeholder="Desde Excel Column 16"
                />
              </label>
            </div>
            )}
            {errorModal && <p style={{ color: '#c62828', margin: '0 0 12px', fontSize: '14px' }}>{errorModal}</p>}
            <div style={{ ...styles.row, marginBottom: 0 }}>
              <button type="button" style={styles.btnPrimary} onClick={handleSaveInscrito} disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
              <button type="button" style={styles.btnGhost} onClick={closeEdit} disabled={saving}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div style={styles.modalOverlay} onClick={closeCreateModal}>
          <div style={{ ...styles.modalBox, maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: 700 }}>Agregar atleta al evento</h3>
            <p style={{ margin: '0 0 16px', color: '#666', fontSize: 13 }}>
              Mismos datos que el formulario de inscripción. El pago no se cobra aquí: lo registra el administrador y la inscripción queda confirmada.
            </p>
            {(() => {
              const showTested = !!evento?.incluye_tested;
              const showUntested = !!evento?.incluye_untested;
              const pesoOpciones = String(formDataCreate.genero || '').toLowerCase() === 'femenino' ? PESO_FEM : PESO_MASC;
              const fieldLabel = { display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 700, color: '#333' };
              const radioList = { display: 'grid', gap: 8, padding: 12, border: '1px solid #eee', borderRadius: 10 };
              const radioItem = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 };
              return (
                <div style={{ display: 'grid', gap: 16 }}>
                  <div>
                    <div style={{ fontWeight: 800, color: '#1a4d8c', marginBottom: 8 }}>DATOS DE COMPETENCIA</div>
                    <div style={{ display: 'grid', gap: 12 }}>
                      <div>
                        <span style={fieldLabel}>DIVISIÓN *</span>
                        <div style={radioList}>
                          {showTested && (
                            <label style={radioItem}>
                              <input type="radio" name="division-admin" checked={formDataCreate.division === 'tested'} onChange={() => setFieldCreate('division', 'tested')} />
                              TESTED
                            </label>
                          )}
                          {showUntested && (
                            <label style={radioItem}>
                              <input type="radio" name="division-admin" checked={formDataCreate.division === 'untested'} onChange={() => setFieldCreate('division', 'untested')} />
                              OPEN (NO TESTEADO)
                            </label>
                          )}
                          {!showTested && !showUntested && <span style={{ color: '#666', fontSize: 13 }}>Este evento no tiene división configurada.</span>}
                        </div>
                      </div>
                      <div>
                        <span style={fieldLabel}>Modalidad *</span>
                        <div style={radioList}>
                          {modalidadesDisponibles.length === 0 && <span style={{ color: '#666', fontSize: 13 }}>Este evento no tiene modalidades configuradas.</span>}
                          {modalidadesDisponibles.map((m) => (
                            <label key={m} style={radioItem}>
                              <input type="radio" name="modalidad-admin" checked={formDataCreate.modalidad === m} onChange={() => setFieldCreate('modalidad', m)} />
                              {LABEL_MODALIDAD[m] || String(m).toUpperCase()}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <span style={fieldLabel}>Modalidad extra</span>
                        <div style={radioList}>
                          {!evento?.modalidad_extra_habilitada ? (
                            <span style={{ color: '#666', fontSize: 13 }}>Este evento no permite modalidad extra.</span>
                          ) : modalidadExtraOpciones.length === 0 ? (
                            <span style={{ color: '#666', fontSize: 13 }}>Este evento no tiene opciones de modalidad extra.</span>
                          ) : (
                            <>
                              {modalidadExtraOpciones.map((o) => (
                                <label key={o.value} style={radioItem}>
                                  <input type="checkbox" checked={formDataCreate.modalidad_extra.includes(o.value)} onChange={() => toggleModalidadExtraCreate(o.value)} />
                                  {o.label}
                                </label>
                              ))}
                              <label style={radioItem}>
                                <input type="checkbox" checked={formDataCreate.modalidad_extra.length === 0} onChange={() => setFieldCreate('modalidad_extra', [])} />
                                NO
                              </label>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div style={{ fontWeight: 800, color: '#1a4d8c', marginBottom: 8 }}>DATOS DEL ATLETA</div>
                    <div style={styles.modalGrid}>
                      <label style={styles.modalFullWidth}>
                        <span style={fieldLabel}>RUT *</span>
                        <input
                          type="text"
                          style={{ ...styles.input, width: '100%' }}
                          value={formDataCreate.rut}
                          onChange={(e) => setFieldCreate('rut', e.target.value)}
                          onBlur={(e) => completarDesdeRut(e.target.value)}
                          placeholder="Ej. 12345678-9"
                        />
                        {lookingUpCreate && <span style={{ color: '#666', fontSize: 12 }}>Buscando datos del atleta…</span>}
                      </label>
                      <label style={styles.modalFullWidth}>
                        <span style={fieldLabel}>Número de afiliación</span>
                        <input style={{ ...styles.input, width: '100%' }} value={formDataCreate.numero_afiliacion} onChange={(e) => setFieldCreate('numero_afiliacion', e.target.value)} />
                      </label>
                      <label>
                        <span style={fieldLabel}>Nombre *</span>
                        <input style={{ ...styles.input, width: '100%' }} value={formDataCreate.nombre} onChange={(e) => setFieldCreate('nombre', e.target.value)} />
                      </label>
                      <label>
                        <span style={fieldLabel}>Apellido *</span>
                        <input style={{ ...styles.input, width: '100%' }} value={formDataCreate.apellido} onChange={(e) => setFieldCreate('apellido', e.target.value)} />
                      </label>
                      <label>
                        <span style={fieldLabel}>Fecha de nacimiento</span>
                        <input type="date" style={{ ...styles.input, width: '100%' }} value={formDataCreate.fecha_nacimiento} onChange={(e) => setFieldCreate('fecha_nacimiento', e.target.value)} />
                      </label>
                      <label>
                        <span style={fieldLabel}>Teléfono</span>
                        <input style={{ ...styles.input, width: '100%' }} value={formDataCreate.telefono} onChange={(e) => setFieldCreate('telefono', e.target.value)} />
                      </label>
                      <label>
                        <span style={fieldLabel}>E-mail</span>
                        <input type="email" style={{ ...styles.input, width: '100%' }} value={formDataCreate.email} onChange={(e) => setFieldCreate('email', e.target.value)} />
                      </label>
                      <label>
                        <span style={fieldLabel}>¿Está afiliado a un club?</span>
                        <input style={{ ...styles.input, width: '100%' }} value={formDataCreate.club_declara} onChange={(e) => setFieldCreate('club_declara', e.target.value)} placeholder="Sin club" />
                      </label>
                    </div>
                  </div>

                  <div>
                    <div style={{ fontWeight: 800, color: '#1a4d8c', marginBottom: 8 }}>CATEGORÍAS</div>
                    <div style={{ display: 'grid', gap: 12 }}>
                      <div>
                        <span style={fieldLabel}>Género *</span>
                        <div style={radioList}>
                          <label style={radioItem}>
                            <input type="radio" name="genero-admin" checked={String(formDataCreate.genero).toLowerCase() === 'femenino'} onChange={() => setFieldCreate('genero', 'Femenino')} />
                            Femenino
                          </label>
                          <label style={radioItem}>
                            <input type="radio" name="genero-admin" checked={String(formDataCreate.genero).toLowerCase() === 'masculino'} onChange={() => setFieldCreate('genero', 'Masculino')} />
                            Masculino
                          </label>
                        </div>
                      </div>
                      <div>
                        <span style={fieldLabel}>Categoría de peso *</span>
                        <div style={radioList}>
                          {!formDataCreate.genero && <span style={{ color: '#666', fontSize: 13 }}>Selecciona género para ver las categorías.</span>}
                          {formDataCreate.genero && pesoOpciones.map((p) => (
                            <label key={p} style={radioItem}>
                              <input type="radio" name="peso-admin" checked={formDataCreate.categoria_peso === p} onChange={() => setFieldCreate('categoria_peso', p)} />
                              {p}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <span style={fieldLabel}>División de edad *</span>
                        <div style={radioList}>
                          {EDAD_OPCIONES.map((o) => (
                            <label key={o.value} style={radioItem}>
                              <input type="radio" name="edad-admin" checked={formDataCreate.division_edad === o.value} onChange={() => setFieldCreate('division_edad', o.value)} />
                              {o.label}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div>
                        <span style={fieldLabel}>División de edad extra</span>
                        <div style={radioList}>
                          {!evento?.edad_extra_habilitada ? (
                            <span style={{ color: '#666', fontSize: 13 }}>Este evento no permite división de edad extra.</span>
                          ) : (
                            <>
                              {EDAD_OPCIONES.filter((o) => o.value !== formDataCreate.division_edad).map((o) => (
                                <label key={o.value} style={radioItem}>
                                  <input type="radio" name="edad-extra-admin" checked={formDataCreate.division_edad_extra === o.value} onChange={() => setFieldCreate('division_edad_extra', o.value)} />
                                  {o.label}
                                </label>
                              ))}
                              <label style={radioItem}>
                                <input type="radio" name="edad-extra-admin" checked={formDataCreate.division_edad_extra === 'no'} onChange={() => setFieldCreate('division_edad_extra', 'no')} />
                                NO
                              </label>
                            </>
                          )}
                        </div>
                      </div>
                      {montoAltaAdmin && (
                        <div style={{ padding: 12, borderRadius: 10, border: '1px solid #e9ecef', background: '#f8f9fa', display: 'grid', gap: 6 }}>
                          <div style={{ fontWeight: 800, color: '#1a4d8c' }}>Monto que cobra el administrador</div>
                          {montoAltaAdmin.items.length === 0 ? (
                            <span style={{ color: '#666', fontSize: 13 }}>Este evento no tiene costo configurado.</span>
                          ) : montoAltaAdmin.items.map((it) => (
                            <div key={it.label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                              <span>{it.label}</span>
                              <span>{formatCLP(it.amount)}</span>
                            </div>
                          ))}
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800 }}>
                            <span>Total</span>
                            <span>{formatCLP(montoAltaAdmin.total)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
            {errorCreateModal && <p style={{ color: '#c62828', margin: '12px 0', fontSize: '14px' }}>{errorCreateModal}</p>}
            <div style={{ ...styles.row, marginBottom: 0, marginTop: 16 }}>
              <button type="button" style={styles.btnPrimary} onClick={handleCreateInscrito} disabled={savingCreate || lookingUpCreate}>
                {savingCreate ? 'Agregando…' : 'Agregar atleta'}
              </button>
              <button type="button" style={styles.btnGhost} onClick={closeCreateModal} disabled={savingCreate}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirmDeleteInscrito}
        onClose={() => setConfirmDeleteInscrito(null)}
        onConfirm={handleConfirmDeleteInscrito}
        title="Eliminar participante"
        message={confirmDeleteInscrito ? `¿Eliminar a ${[confirmDeleteInscrito.nombre, confirmDeleteInscrito.apellido].filter(Boolean).map(formatNombre).join(' ').trim() || '—'} (${formatRut(confirmDeleteInscrito.rut || confirmDeleteInscrito.usuario_id)}) de este evento?` : ''}
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        variant="danger"
      />

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            background: toast.type === 'error' ? '#c62828' : toast.type === 'warning' ? '#b26a00' : '#2e7d32',
            color: '#fff',
            padding: '14px 18px',
            borderRadius: 12,
            boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
            fontWeight: 700,
            fontSize: 14,
            zIndex: 9999,
          }}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default EventoParticipantesView;
